import type {
  FilterBarItem,
  FilterItem,
} from '@wconcept/wconcept-common/models/renewal/common';

export interface LpFilterBarGroups {
  emphasis: FilterBarItem[];
  normal: FilterBarItem[];
}

interface LpSelectedFilterItem {
  filterType?: string;
}

interface LpFilterOption {
  readonly code: string | number;
  readonly name: string;
  readonly count?: number;
}

const CATEGORY_PATH_LENGTH = 3;

export const isLpQuickFilter = (item: Pick<FilterBarItem, 'type'>): boolean =>
  item.type === 'saleTag' || item.type === 'saleTags';

export const getLpSelectedFilterOptions = (
  options: readonly LpFilterOption[] | null | undefined,
  selectedCodes: readonly string[],
  filterType: string
): FilterItem[] => {
  const optionByCode = new Map(
    (options ?? []).map(option => [String(option.code), option])
  );
  const pathCodes =
    filterType === 'category'
      ? (options ?? [])
          .filter(option => selectedCodes.includes(String(option.code)))
          .slice(0, CATEGORY_PATH_LENGTH)
          .map(option => String(option.code))
      : [];
  const codes = [
    ...pathCodes,
    ...new Set(selectedCodes.filter(code => !pathCodes.includes(code))),
  ];

  return codes.flatMap(code => {
    const option = optionByCode.get(code);
    return option
      ? [
          {
            code,
            name: option.name,
            ...(option.count != null ? { count: option.count } : {}),
            filterType,
          },
        ]
      : [];
  });
};

export const sortLpSelectedFiltersByTabs = <T extends LpSelectedFilterItem>(
  selectedFilters: readonly T[],
  tabs: readonly FilterBarItem[]
): T[] => {
  const filterTypes = tabs
    .filter(tab => tab.visible !== false && !isLpQuickFilter(tab))
    .flatMap(tab => {
      if (tab.type === 'brand') return ['brand', 'topBrand', 'newBrand'];
      if (tab.value === 'benefit') return ['productType', 'benefit'];
      return [tab.value];
    });
  const rankByType = new Map(filterTypes.map((type, index) => [type, index]));

  return [...selectedFilters].sort(
    (left, right) =>
      (rankByType.get(left.filterType ?? '') ?? filterTypes.length) -
      (rankByType.get(right.filterType ?? '') ?? filterTypes.length)
  );
};

export const getLpSelectedFilterItems = <T extends LpSelectedFilterItem>(
  list: readonly T[] = []
): T[] => {
  const categoryItems = list.filter(item => item.filterType === 'category');
  const nonCategoryItems = list.filter(item => item.filterType !== 'category');

  let categoriesToAdd: T[] = [];
  if (categoryItems.length > 0 && categoryItems.length < 4) {
    categoriesToAdd = [categoryItems[categoryItems.length - 1]];
  } else if (categoryItems.length >= 4) {
    categoriesToAdd = categoryItems.slice(3);
  }

  return list.filter(
    item => nonCategoryItems.includes(item) || categoriesToAdd.includes(item)
  );
};

export const getLpVisibleSelectedFilters = <T extends LpSelectedFilterItem>(
  selectedFilters: readonly T[],
  tabs: readonly FilterBarItem[],
  hiddenFilterTypes: readonly string[] = []
): T[] =>
  sortLpSelectedFiltersByTabs(
    getLpSelectedFilterItems(selectedFilters).filter(
      filter => !hiddenFilterTypes.includes(filter.filterType ?? '')
    ),
    tabs
  );

export const getLpQuickFilterTextColor = (
  value: string
): string | undefined => {
  if (value === 'todayDelivery') return '#000';

  return /^\d+$/.test(value) ? '#ff4600' : undefined;
};

export const getLpFilterBarGroups = (
  list: readonly FilterBarItem[]
): LpFilterBarGroups => {
  const emphasis = list.filter(isLpQuickFilter);
  const normal = list.filter(item => !isLpQuickFilter(item));

  return { emphasis, normal };
};
